import React from "react";
import { Box, Flex, FlexProps, Image, Text } from "@chakra-ui/react";
import { useLandingTheme } from "../LandingThemeContext";
import { useLandingCopy } from "../LandingLanguageContext";
import { ContentContainer, PrimaryCallToAction, SectionHeading } from "./shared";

// Colours lifted from the app's multiple-choice question UI, so the mock reads as the product.
const APP_UI = {
  font: '"Outfit", sans-serif',
  body: "#3D3E3D",
  stemBg: "#E4FFD1",
  stemBorder: "#D2EBC0",
  optionBorder: "#E1EBE5",
  correctBg: "#EDFFF2",
  correctBorder: "#99E1AD",
};

// A real USMLE Step 1 question Jungle generated (cards_saved, template usmle_step1; GI / pharmacology,
// difficulty band B3). Options are listed alphabetically, the way Step 1 orders them.
// Stays in English on the Spanish page on purpose: USMLE Step 1 is taken in English, so this is
// what the questions Jungle writes for that exam look like.
const STEP1_QUESTION = {
  vignette:
    "A 52-year-old man with a 15-year history of alcohol use disorder comes to the emergency department because of a 1-day history of right upper quadrant pain and nausea. He reports taking two 500-mg tablets of acetaminophen every 4 to 6 hours for the past 3 days for a toothache. Temperature is 37.2°C (99.0°F), pulse is 98/min, and blood pressure is 112/76 mm Hg. Physical examination shows scleral icterus and tenderness to palpation in the right upper quadrant. Laboratory studies show AST 3,500 U/L and ALT 3,200 U/L.",
  stem: "Which of the following best explains this patient's increased susceptibility to this toxicity?",
  options: [
    "Induction of cytochrome P450 enzymes",
    "Inhibition of alcohol dehydrogenase",
    "Inhibition of glutathione reductase",
    "Stimulation of glucuronidation pathways",
    "Uncoupling of oxidative phosphorylation",
  ],
  correctOptionIndex: 0,
};

// The generated vignette in the app's own question UI, tilted like the card in the Original layout.
function Step1QuestionCardMock() {
  const theme = useLandingTheme();
  return (
    <Box lang="en" dir="ltr" w={{ base: "100%", lg: "470px" }} maxW="470px" transform={{ base: "none", lg: "rotate(4deg)" }} mr={{ lg: "24px" }} bg="#FFFFFF" borderRadius={theme.shape.cardRadius} p={{ base: "16px", md: "26px" }} boxShadow="0 24px 60px rgba(0, 40, 20, 0.14)" fontFamily={APP_UI.font} color={APP_UI.body}>
      <Text fontSize={{ base: "12.5px", md: "13.5px" }} lineHeight={1.5} mb={{ base: "10px", md: "14px" }}>
        {STEP1_QUESTION.vignette}
      </Text>
      <Box bg={APP_UI.stemBg} border={`1px solid ${APP_UI.stemBorder}`} borderRadius="14px" px="16px" py="13px" boxShadow="0px 2px 20px 0px rgba(0, 0, 0, 0.06), 0px 1px 2px 0px rgba(0, 0, 0, 0.05)">
        <Text fontSize="15px" lineHeight="124%">
          {STEP1_QUESTION.stem}
        </Text>
      </Box>
      <Flex direction="column" gap="7px" mt="12px" px="4px">
        {STEP1_QUESTION.options.map((option, optionIndex) => {
          const isCorrect = optionIndex === STEP1_QUESTION.correctOptionIndex;
          return (
            <Box key={option} px="16px" py={{ base: "8px", md: "10px" }} borderRadius="14px" bg={isCorrect ? APP_UI.correctBg : "#FFFFFF"} border={`1px solid ${isCorrect ? APP_UI.correctBorder : APP_UI.optionBorder}`} boxShadow={isCorrect ? "0px 1.5px 1.5px 0px rgba(135, 225, 160, 0.32)" : "0px 1px 2px 0px rgba(8, 47, 19, 0.08)"}>
              <Text fontSize={{ base: "13px", md: "14px" }} lineHeight="124%">
                {"ABCDE"[optionIndex]}. {option}
              </Text>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}

function CapybaraReading(props: FlexProps) {
  return (
    <Flex justify={{ base: "center", lg: "flex-start" }} {...props}>
      <Image src="/images/landing/inventory/capybara-reading.svg" alt="" w={{ base: "160px", lg: "210px" }} aria-hidden="true" />
    </Flex>
  );
}

// Exam-style templates: pick the exam, get questions written the way that exam writes them.
export default function ExamStyleSection() {
  const theme = useLandingTheme();
  const examStyleCopy = useLandingCopy().examStyle;
  return (
    <Box as="section" pt={{ base: "170px", md: "190px", lg: "240px" }}>
      <ContentContainer>
        <Flex direction={{ base: "column", lg: "row" }} align="center" justify="space-between" gap={{ base: "40px", lg: 10 }} px={{ lg: "16px" }}>
          <Box maxW={{ base: "100%", lg: "480px" }} w="100%">
            <SectionHeading textAlign={{ base: "center", lg: "left" }} fontSize={{ base: "20px", lg: `${Math.round(36 * theme.heading.sizeScale)}px` }}>
              {examStyleCopy.heading}
            </SectionHeading>
            <Text fontFamily={theme.font.body} fontSize={{ base: "18px", lg: "20px" }} fontWeight={500} color={theme.color.text} textAlign={{ base: "center", lg: "left" }} mt={{ base: "20px", lg: "24px" }}>
              {examStyleCopy.supportedExamsLine}
            </Text>
            {/* The capybara, reading up for the exam beside the card (on a phone it sits under the card). */}
            <CapybaraReading display={{ base: "none", lg: "flex" }} mt="52px" />
          </Box>

          <Step1QuestionCardMock />
          <CapybaraReading display={{ base: "flex", lg: "none" }} mt="-8px" />
        </Flex>

        <PrimaryCallToAction mt={{ base: "44px", md: "63px", lg: "81px" }} />
      </ContentContainer>
    </Box>
  );
}
